import {
  BILLING_STATUS,
  SubEngineInstallmentStatusEnum,
  SubEngineSubscriptionStatusEnum,
  SUBSCRIPTION_PAYMENT_PLAN,
  SUBSCRIPTION_STATUS,
  TypeSubEngineSubscription,
  TypeSubscriptionRecord,
  TypeSubscriptionStatus,
} from '@/lib/api/subscriptionManager/types';
import {
  isBabylistSubscription,
  isGroupGiftingMigratedSubscription,
} from '@/lib/api/subscriptionManager/helpers/subscriptionSourceHelpers';
import { getScheduledItems } from '@/lib/api/subscriptionManager/helpers/scheduledItemHelpers';
import { TypeUserRecord } from '@/lib/api/accountManager/types';

export function filterInvalidSubscriptionTypes(
  subscriptions: TypeSubscriptionRecord[],
) {
  const invalidStatuses = new Set([
    SUBSCRIPTION_STATUS.INACTIVE,
    SUBSCRIPTION_STATUS.CREATED,
  ]);

  return subscriptions?.filter(
    (s) => !invalidStatuses.has(s.subscription_status),
  );
}

export function filterHiddenSubscriptions(
  subscriptions: TypeSubscriptionRecord[],
  user: TypeUserRecord,
) {
  const validSubscriptions = filterInvalidSubscriptionTypes(subscriptions);

  return validSubscriptions.filter((subscription) => {
    const child = user.children.find(
      (child) => child.uuid === subscription.child_uuid,
    );

    if (!child) {
      return true;
    }

    return !child.is_hidden;
  });
}

export function filterValidSubscriptionTypes(
  subscriptions: TypeSubscriptionRecord[],
) {
  const validStatuses = new Set([
    SUBSCRIPTION_STATUS.ACTIVE,
    SUBSCRIPTION_STATUS.FINISHED,
    SUBSCRIPTION_STATUS.CANCELLED,
  ]);

  return subscriptions?.filter((s) => validStatuses.has(s.subscription_status));
}

export function filterCancelledSubscriptions(
  subscriptions: TypeSubscriptionRecord[],
) {
  return subscriptions?.filter(isSubscriptionCancelled);
}

export function isActiveSubscriber(subscriptions: TypeSubscriptionRecord[]) {
  return subscriptions?.some(isSubscriptionActive);
}

export function isAgedOutSubscriber(subscriptions: TypeSubscriptionRecord[]) {
  return subscriptions?.some(isSubscriptionFinished);
}

export function isAgedOutFullSubscriber(
  subscriptions: TypeSubscriptionRecord[],
) {
  return subscriptions?.some(isSubscriptionReactivationAllowed);
}

export function isSubscriptionActive(subscription: TypeSubscriptionRecord) {
  return subscription.subscription_status === SUBSCRIPTION_STATUS.ACTIVE;
}

export function isSubEngineSubscriptionActive(
  subscription: TypeSubEngineSubscription,
) {
  return subscription.status === SubEngineSubscriptionStatusEnum.ACTIVE;
}

export function isSubEngineSubscriptionCancelled(
  subscription: TypeSubEngineSubscription,
) {
  return subscription.status === SubEngineSubscriptionStatusEnum.CANCELLED;
}

export function isSubEngineSubscriptionPaused(
  subscription: TypeSubEngineSubscription,
) {
  return subscription.status === SubEngineSubscriptionStatusEnum.PAUSED;
}

export function isSubscriptionCancelled(subscription: TypeSubscriptionRecord) {
  return subscription.subscription_status === SUBSCRIPTION_STATUS.CANCELLED;
}

export function isSubscriptionFinished(subscription: TypeSubscriptionRecord) {
  return subscription.subscription_status === SUBSCRIPTION_STATUS.FINISHED;
}

export function isSubEngineSubscriptionProcessing(
  subscription: TypeSubEngineSubscription,
) {
  const installmentsAllPending = subscription.installments?.every(
    (installment) =>
      installment.status === SubEngineInstallmentStatusEnum.SCHEDULED,
  );
  return (
    subscription.status === SubEngineSubscriptionStatusEnum.ACTIVE &&
    installmentsAllPending
  );
}

export function isSubscriptionCancellationAllowed(
  subscription: TypeSubscriptionRecord,
) {
  return isSubscriptionActive(subscription);
}

export function isSubscriptionOnlyCancellableWithCXIntervention(
  subscription: TypeSubscriptionRecord,
) {
  return (
    isBabylistSubscription(subscription) ||
    isGroupGiftingMigratedSubscription(subscription)
  );
}

export function isSubscriptionReactivationAllowed(
  subscription: TypeSubscriptionRecord,
) {
  const { CANCELLED, FINISHED } = SUBSCRIPTION_STATUS;
  const isPartialSubscription = subscription.partial_subscription;

  return (
    [CANCELLED, FINISHED].includes(subscription.subscription_status) &&
    !isPartialSubscription
  );
}

export function determineSubscriptionPaymentPlan(
  subscription: TypeSubscriptionRecord,
) {
  const isRepufEnabledForSubscription = subscription.bundle_discount_uuid;
  const unprocessedScheduledItems = getScheduledItems(subscription, {
    filterProcessedItems: true,
  });
  const nextUnprocessedItem = unprocessedScheduledItems?.[0];

  if (nextUnprocessedItem?.billing_status === BILLING_STATUS.PREPAY) {
    if (isRepufEnabledForSubscription) {
      return SUBSCRIPTION_PAYMENT_PLAN.REPUF;
    }

    return SUBSCRIPTION_PAYMENT_PLAN.PUF;
  } else {
    return SUBSCRIPTION_PAYMENT_PLAN.PAYGO;
  }
}

// Priority order: ACTIVE > FINISHED > CREATED > CANCELLED
const statusPriority: Record<TypeSubscriptionStatus, number> = {
  [SUBSCRIPTION_STATUS.ACTIVE]: 4,
  [SUBSCRIPTION_STATUS.FINISHED]: 3,
  [SUBSCRIPTION_STATUS.CREATED]: 2,
  [SUBSCRIPTION_STATUS.CANCELLED]: 1,
  [SUBSCRIPTION_STATUS.INACTIVE]: 0,
  [SUBSCRIPTION_STATUS.ERROR]: 0,
};

export function getSubscriptionStatusSummary(
  subscriptions: TypeSubscriptionRecord[],
) {
  const summary = {} as Record<string, { status: TypeSubscriptionStatus }>;

  subscriptions.forEach(({ subscription_type, subscription_status }) => {
    const { status } = summary[subscription_type] ?? {};
    if (status) {
      const currentPriority = statusPriority[status];
      const newPriority = statusPriority[subscription_status];
      if (newPriority > currentPriority) {
        summary[subscription_type] = { status: subscription_status };
      }
    } else {
      summary[subscription_type] = { status: subscription_status };
    }
  });

  return summary;
}
